/** Formatting shared by every interface: locale-aware, a space between number and unit, 24-hour time. */
export type Locale = 'nl' | 'en';

const tag = (locale: Locale) => (locale === 'nl' ? 'nl-NL' : 'en-GB');

export function formatNumber(value: number, locale: Locale, digits = 0): string {
  return new Intl.NumberFormat(tag(locale), { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    value,
  );
}

/** 880 → "880 W", 2350 → "2,35 kW". */
export function formatPower(watts: number, locale: Locale): { value: string; unit: 'W' | 'kW' } {
  const abs = Math.abs(watts);
  if (abs < 1000) return { value: formatNumber(abs, locale), unit: 'W' };
  return { value: formatNumber(abs / 1000, locale, abs < 10_000 ? 2 : 1), unit: 'kW' };
}

export function formatKwh(kwh: number, locale: Locale, digits = kwh < 100 ? 2 : 0): string {
  return `${formatNumber(kwh, locale, digits)} kWh`;
}

export function formatAmps(amps: number, locale: Locale): string {
  return `${formatNumber(amps, locale, 1)} A`;
}

export function formatVolts(volts: number, locale: Locale): string {
  return `${formatNumber(volts, locale, 1)} V`;
}

export function formatTime(iso: string, locale: Locale, withSeconds = false): string {
  return new Intl.DateTimeFormat(tag(locale), {
    hour: '2-digit',
    minute: '2-digit',
    second: withSeconds ? '2-digit' : undefined,
    hour12: false,
  }).format(new Date(iso));
}

export function formatDate(iso: string, locale: Locale, style: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat(
    tag(locale),
    style === 'short' ? { day: 'numeric', month: 'short' } : { weekday: 'long', day: 'numeric', month: 'long' },
  ).format(new Date(iso));
}

export function formatDateTime(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(tag(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

const UNITS: [limit: number, seconds: number, nl: [string, string], en: [string, string]][] = [
  [90, 1, ['seconde', 'seconden'], ['second', 'seconds']],
  [90 * 60, 60, ['minuut', 'minuten'], ['minute', 'minutes']],
  [36 * 3600, 3600, ['uur', 'uur'], ['hour', 'hours']],
  [Infinity, 86_400, ['dag', 'dagen'], ['day', 'days']],
];

/** "18 seconds", "3 hours" — a duration in words, for "last seen … ago". */
export function formatDuration(seconds: number, locale: Locale): string {
  const s = Math.max(0, Math.round(seconds));
  for (const [limit, size, nl, en] of UNITS) {
    if (s < limit) {
      const n = Math.max(1, Math.round(s / size));
      const words = locale === 'nl' ? nl : en;
      return `${n} ${n === 1 ? words[0] : words[1]}`;
    }
  }
  return '';
}

export function secondsSince(iso: string | null, now: Date): number | null {
  return iso ? Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000) : null;
}

/** "18 seconds ago" / "18 seconden geleden"; "never" when there is no timestamp. */
export function formatAgo(iso: string | null, now: Date, locale: Locale): string {
  const s = secondsSince(iso, now);
  if (s === null) return locale === 'nl' ? 'nog nooit' : 'never';
  return locale === 'nl' ? `${formatDuration(s, locale)} geleden` : `${formatDuration(s, locale)} ago`;
}

/** Compact professional form: "18 s", "4 min", "3 h", "2 d". */
export function formatAgoShort(iso: string | null, now: Date): string {
  const s = secondsSince(iso, now);
  if (s === null) return '–';
  if (s < 90) return `${Math.round(s)} s`;
  if (s < 90 * 60) return `${Math.round(s / 60)} min`;
  if (s < 36 * 3600) return `${Math.round(s / 3600)} h`;
  return `${Math.round(s / 86_400)} d`;
}

export function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86_400);
  const h = Math.floor((seconds % 86_400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return d > 0 ? `${d} d ${h} h` : h > 0 ? `${h} h ${m} min` : `${m} min`;
}
